import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { landingPath } from "../lib/nav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, Mail, Lock, Eye, EyeOff, Globe,
  Store, BarChart3, ShieldCheck, Users, MapPin,
} from "lucide-react";

const BURGUNDY = "#6B1717";
const BURGUNDY_DEEP = "#4A0E0E";
const GOLD = "#C49A4A";
const GOLD_SOFT = "#E7D0AD";
const CREAM = "#FAF7F0";
const CHARCOAL = "#3B2722";
const MUTED = "#765A4A";

const FEATURES = [
  { icon: Store, title: "All-in-One", desc: "POS, Kitchen, Inventory, Procurement, HR, Accounting & more." },
  { icon: BarChart3, title: "Real-time Insights", desc: "Live dashboard and intelligence for faster, smarter decisions." },
  { icon: ShieldCheck, title: "Secure & Reliable", desc: "Enterprise-grade security with role-based access control." },
  { icon: Users, title: "Multi-Location", desc: "Manage all locations seamlessly from one central system." },
];

const FOOD_IMG =
  "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=80";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back to Sir Monty");
      navigate(landingPath(u));
    } catch (err) {
      const m = formatApiError(err);
      setError(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  const comingSoon = (what) => () => toast.info(`${what} is coming soon.`);

  return (
    <div className="min-h-screen flex" style={{ background: CREAM }}>
      {/* ============ LEFT PANEL ============ */}
      <div
        className="hidden lg:flex w-1/2 relative flex-col justify-between p-10 xl:p-14 overflow-hidden"
        style={{
          background: `radial-gradient(circle at 65% 30%, ${BURGUNDY} 0%, ${BURGUNDY_DEEP} 75%)`,
        }}
      >
        {/* Watermark crest */}
        <img
          src="/logo.jpg"
          alt=""
          aria-hidden
          className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 h-[520px] w-[520px] object-contain opacity-[0.08]"
        />

        {/* Food image (upper-right) */}
        <div
          className="pointer-events-none absolute -top-10 -right-10 h-[520px] w-[520px] rounded-full overflow-hidden"
          style={{
            boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
            border: `1px solid ${GOLD}33`,
          }}
        >
          <img src={FOOD_IMG} alt="" className="h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 30% 40%, rgba(0,0,0,0) 45%, rgba(74,14,14,0.35) 100%)",
            }}
          />
        </div>

        {/* Top: Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-2">
            <img src="/logo.jpg" alt="Sir Monty" className="h-12 w-12 object-contain" />
          </div>
          <div className="leading-none">
            <div className="font-brand text-white text-[28px] font-bold">Sir Monty</div>
            <div className="text-[11px] tracking-[0.3em] mt-1" style={{ color: GOLD }}>
              ERP
              <span className="inline-block ml-2 align-middle w-8 h-px" style={{ background: GOLD }} />
            </div>
          </div>
        </div>

        {/* Middle: Hero */}
        <div className="relative z-10 max-w-[520px]">
          <h2 className="font-brand font-bold text-white leading-[1.05] text-5xl xl:text-[56px]">
            Great Food,
            <br />
            <span style={{ color: GOLD }}>Great People,</span>
            <br />
            Great Value.
          </h2>
          <div className="w-16 h-[3px] mt-6 rounded-full" style={{ background: GOLD }} />
          <p className="mt-6 text-[15px] leading-relaxed max-w-md" style={{ color: `${GOLD_SOFT}CC` }}>
            One integrated platform for POS, Kitchen, Inventory, Procurement, HR &amp; Payroll,
            Accounting and more — across Uyo, Ikot Ekpene, Calabar &amp; Lekki.
          </p>
        </div>

        {/* Feature tiles */}
        <div className="relative z-10">
          <div
            className="grid grid-cols-4 gap-3 rounded-2xl p-5"
            style={{
              background: "rgba(0,0,0,0.28)",
              border: `1px solid ${GOLD}22`,
              backdropFilter: "blur(6px)",
            }}
          >
            {FEATURES.map((f) => (
              <div key={f.title} className="text-center px-2">
                <div
                  className="mx-auto flex items-center justify-center h-11 w-11 rounded-full mb-3"
                  style={{ background: `${GOLD}1F`, border: `1px solid ${GOLD}44` }}
                >
                  <f.icon size={20} style={{ color: GOLD }} />
                </div>
                <div className="text-white font-semibold text-[13px]">{f.title}</div>
                <div className="mt-1 text-[11px] leading-snug" style={{ color: `${GOLD_SOFT}AA` }}>
                  {f.desc}
                </div>
              </div>
            ))}
          </div>

          {/* Locations */}
          <div
            className="mt-4 flex items-center gap-3 rounded-2xl px-5 py-4"
            style={{ background: "rgba(0,0,0,0.28)", border: `1px solid ${GOLD}22` }}
          >
            <div
              className="flex items-center justify-center h-9 w-9 rounded-full"
              style={{ background: `${GOLD}1F`, border: `1px solid ${GOLD}44` }}
            >
              <MapPin size={16} style={{ color: GOLD }} />
            </div>
            <div className="leading-tight">
              <div className="text-[11px] tracking-wider uppercase" style={{ color: `${GOLD_SOFT}AA` }}>
                Operating across
              </div>
              <div className="text-white font-semibold text-[14px]">
                Uyo &nbsp;•&nbsp; Ikot Ekpene &nbsp;•&nbsp; Calabar &nbsp;•&nbsp; Lekki
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: `${GOLD_SOFT}66` }}>
          <span>© 2026 Sir Monty Foods Ltd. All rights reserved.</span>
          <span className="opacity-60">·</span>
          <span className="inline-flex items-center gap-1" data-testid="crafted-by">
            Crafted by
            <a href="https://7thedge.ng" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline" style={{ color: GOLD }}>7thEdge Solutions</a>
          </span>
        </div>
      </div>

      {/* ============ RIGHT PANEL ============ */}
      <div className="flex-1 flex flex-col px-6 py-6 sm:px-10 lg:px-14" style={{ background: CREAM }}>
        {/* Language selector */}
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="lang-select"
            onClick={comingSoon("Language selection")}
            className="flex items-center gap-2 rounded-full bg-white border border-[#EADFCB] px-4 py-2 text-sm font-medium hover:border-[#C49A4A] transition-colors"
            style={{ color: CHARCOAL }}
          >
            <Globe size={16} style={{ color: BURGUNDY }} />
            English
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5L5 6.5L8 3.5" stroke={CHARCOAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[440px]">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center justify-center mb-8">
              <img src="/logo.jpg" alt="Sir Monty" className="h-16 w-auto object-contain" />
            </div>

            <h1 className="font-brand font-bold text-[42px] leading-none flex items-center gap-3" style={{ color: CHARCOAL }}>
              Welcome back
              <span className="inline-block" role="img" aria-label="wave">👋</span>
            </h1>
            <p className="mt-3 text-[15px]" style={{ color: MUTED }}>
              Sign in to access your enterprise dashboard
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] font-semibold" style={{ color: CHARCOAL }}>
                  Email address
                </Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email address"
                    className="h-12 pl-11 rounded-xl bg-white border-[#E9DFCF] focus:border-[#C49A4A] focus-visible:ring-[#C49A4A]/30"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[13px] font-semibold" style={{ color: CHARCOAL }}>
                  Password
                </Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    className="h-12 pl-11 pr-11 rounded-xl bg-white border-[#E9DFCF] focus:border-[#C49A4A] focus-visible:ring-[#C49A4A]/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    data-testid="toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-[#F3ECE2]"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={18} style={{ color: MUTED }} /> : <Eye size={18} style={{ color: MUTED }} />}
                  </button>
                </div>
              </div>

              {/* Remember / Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={remember}
                    onCheckedChange={(v) => setRemember(!!v)}
                    data-testid="remember-me"
                    className="data-[state=checked]:bg-[#8B1E1E] data-[state=checked]:border-[#8B1E1E]"
                  />
                  <span className="text-[14px]" style={{ color: CHARCOAL }}>Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={comingSoon("Password recovery")}
                  data-testid="forgot-password"
                  className="text-[14px] font-semibold hover:underline"
                  style={{ color: BURGUNDY }}
                >
                  Forgot password?
                </button>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="text-sm rounded-lg px-3 py-2"
                  style={{ color: "#A52222", background: "#F7E5E5", border: "1px solid #eecccc" }}
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full h-12 text-[15px] font-semibold text-white rounded-xl hover:opacity-95 shadow-sm"
                style={{ background: "#8B1E1E" }}
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>Sign in <ArrowRight size={18} className="ml-1.5" /></>
                )}
              </Button>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
}
